<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SalesDetailDocusignTimeline from './SalesDetailDocusignTimeline.vue';
import SalesReceiptEnvelopeActions from './SalesReceiptEnvelopeActions.vue';

const props = defineProps({
  canManage: { type: Boolean, default: false },
  integration: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  propertyOfferId: { type: [Number, String], default: null },
  resendingSignerReference: { type: String, default: '' },
});

const emit = defineEmits(['refresh', 'resend-signer', 'updated']);
const { t } = useI18n();
const pendingCount = computed(() => props.integration?.pending_count || 0);
const isComplete = computed(
  () => props.integration && pendingCount.value === 0
);
const isConfigured = computed(
  () => props.integration?.status !== 'not_configured'
);
</script>

<template>
  <main class="mx-auto w-full max-w-[1080px] px-4 pb-8 sm:px-6 lg:px-10">
    <section
      class="rounded-lais border border-lais-border-soft bg-white p-5 shadow-lais-card sm:p-6"
    >
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <span
            class="rounded-full px-2.5 py-1 text-[10px] font-bold tracking-[0.14em]"
            :class="
              isComplete
                ? 'bg-lais-success/15 text-lais-success'
                : 'bg-lais-warning/15 text-amber-800'
            "
          >
            {{
              isComplete
                ? t('SALES.DELIVERY.CHECKOUT.STATUS.READY')
                : t('SALES.DELIVERY.CHECKOUT.STATUS.IN_PROGRESS')
            }}
          </span>
          <h1 class="mt-4 text-xl font-semibold text-lais-text">
            {{ t('SALES.DELIVERY.CHECKOUT.TITLE') }}
          </h1>
        </div>
        <SalesReceiptEnvelopeActions
          v-if="integration && isConfigured"
          :integration="integration"
          :property-offer-id="propertyOfferId"
          :can-manage="canManage"
          @updated="emit('updated', $event)"
        />
      </div>

      <p
        v-if="!integration || !isConfigured"
        class="mt-5 rounded-lais-sm bg-amber-50 p-4 text-sm text-amber-800"
      >
        {{ t('SALES.DELIVERY.CHECKOUT.NOT_CONFIGURED') }}
      </p>
      <template v-else>
        <div class="mt-5 border-t border-lais-border-soft pt-5">
          <SalesDetailDocusignTimeline
            :integration="integration"
            :loading="loading"
            :can-resend="canManage"
            :resending-signer-reference="resendingSignerReference"
            @refresh="emit('refresh')"
            @resend-signer="emit('resend-signer', $event)"
          />
        </div>
        <p
          v-if="isComplete"
          class="mt-5 flex items-center gap-2 text-sm font-semibold text-lais-success"
        >
          <span class="i-lucide-circle-check size-5" aria-hidden="true" />
          {{ t('SALES.DELIVERY.CHECKOUT.COMPLETE') }}
        </p>
      </template>
    </section>
  </main>
</template>
